import { inject, observer } from 'mobx-react';
import React from 'react';

import { DataStoreType } from 'models/DataStore';
import { AiModulesStoreType } from 'models/AiModulesStore';
import { ProjectAiModulesStoreType } from 'models/ProjectAiModulesStore';
import { ActionsStoreType } from 'models/ActionsStore';
import { FireStoreType } from 'models/FireStore';
import ProjectLayout, { ProjectLayoutProps } from './ProjectLayout';
import AiModulesSidebarComponent from 'screens/FireScreen/components/AiModulesSidebarComponent';
import Columns from 'components/Layout/Columns';
import { GapSizes } from 'utils/constants/gap-sizes';
import { history } from 'utils/history/history';
import { ROUTE_PROJECT } from 'utils/constants/routes';

interface PublicContextAwareProjectLayoutProps {
  active?: ProjectLayoutProps['active'];
  children?: ProjectLayoutProps['children'];
}

interface ContextAwareProjectLayoutProps {
  dataStore: DataStoreType;
  aiModulesStore: AiModulesStoreType;
  projectAiModulesStore: ProjectAiModulesStoreType;
  actionsStore: ActionsStoreType;
  fireStore: FireStoreType;
}

@inject(
  'dataStore',
  'aiModulesStore',
  'projectAiModulesStore',
  'actionsStore',
  'fireStore'
)
@observer
class ContextAwareProjectLayout extends React.Component<
  ContextAwareProjectLayoutProps & PublicContextAwareProjectLayoutProps,
  { isPowerpointLoading: boolean }
> {
  state = { isPowerpointLoading: false };

  componentDidMount() {
    const { projectAiModulesStore, dataStore } = this.props;
    const orgId = dataStore.currentOrganizationId;
    const projectId = dataStore.currentProject?.id;

    if (orgId && projectId) {
      projectAiModulesStore.getProjectAiModules(orgId, projectId);
    }
  }

  downloadFullPowerpoint = async () => {
    const { actionsStore, dataStore } = this.props;
    const projectId = dataStore.currentProject?.id;
    const fireId = dataStore.fire?.id;

    if (!projectId) {
      return;
    }

    this.setState({ isPowerpointLoading: true });
    await actionsStore.downloadPowerpointProject(projectId, fireId);
    this.setState({ isPowerpointLoading: false });
  };

  deleteFirePowerpoint = async () => {
    const { fireStore, dataStore } = this.props;
    const fireId = dataStore.fire?.id;
    const projectId = dataStore.currentProject?.id;

    if (!fireId || !projectId) {
      return;
    }

    await fireStore.deleteFirePowerpoint(fireId, projectId);
  };

  convertFireToProject = async () => {
    const { fireStore, dataStore } = this.props;
    const fire = dataStore.fire;
    const organizationId = dataStore.currentOrganizationId;

    if (!fire?.id || !organizationId || !fire.project_id) {
      return;
    }

    await fireStore.convertFireToProject(fire.id);

    history.push(
      ROUTE_PROJECT.replace(':orgId', organizationId.toString()).replace(
        ':projectId',
        fire.project_id.toString()
      )
    );
  };

  render() {
    const {
      dataStore,
      active,
      children,
      aiModulesStore,
      projectAiModulesStore
    } = this.props;

    if (dataStore.currentProject?.is_fire_project) {
      // @ts-ignore
      return (
        <Columns columnTemplate="1fr 22.1875rem" gap={GapSizes.NONE}>
          <main className="fire-layout-item-detail">{children}</main>

          <AiModulesSidebarComponent
            aiModulesStore={aiModulesStore}
            projectAiModulesStore={projectAiModulesStore}
            hypotheses={dataStore.fire?.hypothesisList || []}
            organizationId={dataStore.currentOrganizationId!}
            projectId={dataStore.currentProject?.id}
            fireStrategy={dataStore.fire?.fire_strategy}
            onDownloadPowerpoint={() => this.downloadFullPowerpoint()}
            onDeletePowerpoint={() => this.deleteFirePowerpoint()}
            onCopyToProject={() => this.convertFireToProject()}
            isPowerpointLoading={this.state.isPowerpointLoading}
            powerpointUrl={dataStore.fire?.powerpoint_url}
            tasksRunning={
              dataStore.fire ? !dataStore.fire.allTasksDone : false
            }
          />
        </Columns>
      );
    }

    return (
      <ProjectLayout
        project={dataStore.currentProject!}
        org={dataStore.currentOrganization}
        active={active}
      >
        {children}
      </ProjectLayout>
    );
  }
}

export default (props: PublicContextAwareProjectLayoutProps) => (
  // @ts-ignore
  <ContextAwareProjectLayout {...props} />
);
